import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { erpCreate, erpUpdate, erpGet } from '../../../services/erpApi';
import { Input, Select, Textarea, fmtInr } from '../ui';
import { Picker } from '../components/Picker';
import { DocForm, Section } from '../components/DocForm';
import { STATE_OPTS, stateCode, stateFromGstin, POTENTIAL_CATEGORIES, GST_TYPES, CUSTOMER_SEGMENTS, VENDOR_SEGMENTS, PAYMENT_TERMS } from '../components/india';

const EMPTY = {
  code: '', name: '', segment: '', gst_type: 'Regular', gstin: '', pan: '', state: '', state_code: '', city: '', pincode: '', country: 'India', address1: '', address2: '',
  ship_same: true, ship_address1: '', ship_address2: '', ship_city: '', ship_state: '', ship_pincode: '',
  contact: '', designation: '', phone: '', alt_phone: '', whatsapp: '', email: '', website: '',
  credit_limit: '', credit_days: 30, payment_terms: '30 Days', bank_name: '', bank_account: '', bank_ifsc: '', msme_no: '', notes: '',
  potential: Object.fromEntries(POTENTIAL_CATEGORIES.map((c) => [c, ''])),
};

const fromDoc = (d) => ({ ...EMPTY, ...Object.fromEntries(Object.entries(d).filter(([k]) => k in EMPTY)), potential: { ...EMPTY.potential, ...(d.potential || {}) },
  ship_same: !d.ship_address1 && !d.ship_city });

// Full Customer / Vendor master form (Flow-style sections, close guard).
export const PartyForm = ({ kind, initial, onClose, onSaved }) => {
  const isCust = kind === 'customers';
  const label = isCust ? 'Customer' : 'Vendor';
  const [f, setF] = useState(() => (initial ? fromDoc(initial) : EMPTY));
  const [busy, setBusy] = useState(false);
  const [autoCode, setAutoCode] = useState('');
  const t = `party-form`;

  useEffect(() => { if (!initial) erpGet(`${kind}/next-code`).then((r) => setAutoCode(r.data.code)).catch(() => {}); }, [kind, initial]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setV = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onGstin = (e) => {
    const g = e.target.value.toUpperCase();
    const st = stateFromGstin(g);
    setF((s) => ({ ...s, gstin: g, pan: g.length >= 12 ? g.slice(2, 12) : s.pan, ...(st && !s.state ? { state: st, state_code: g.slice(0, 2) } : {}) }));
  };
  const potTotal = Object.values(f.potential).reduce((a, v) => a + (+v || 0), 0);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial ? fromDoc(initial) : EMPTY);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, state_code: stateCode(f.state), credit_limit: +f.credit_limit || 0, credit_days: +f.credit_days || 0 };
      if (f.ship_same) Object.assign(body, { ship_address1: '', ship_address2: '', ship_city: '', ship_state: '', ship_pincode: '' });
      delete body.ship_same;
      const r = initial ? await erpUpdate(kind, initial.id, body) : await erpCreate(kind, body);
      toast.success(`${label} ${r.data.code} ${initial ? 'updated' : 'created'}`); onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <DocForm open onClose={onClose} dirty={dirty} busy={busy} onSubmit={submit} testid={t} title={initial ? `Edit ${label} — ${initial.code}` : `New ${label}`}
      subtitle={`${label} master · identity is hidden from restricted roles, only the code is visible to them`} submitLabel={initial ? `Update ${label}` : `Create ${label}`}
      footerLeft={<span style={{ color: 'var(--erp-muted)' }}>Code <b style={{ color: 'var(--erp-ink)' }}>{initial?.code || f.code || autoCode || '…'}</b>{isCust && potTotal > 0 && <> · Potential <b style={{ color: 'var(--erp-ink)' }}>{fmtInr(potTotal)}/yr</b></>}</span>}>

      <Section title="Identity">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input label={`${label} code`} value={initial ? initial.code : f.code} onChange={set('code')} disabled={!!initial} placeholder={autoCode || 'Auto'} hint={initial ? 'Code is permanent' : `Leave blank for ${autoCode || 'auto'}`} data-testid={`${t}-code`} />
          <Input label="Legal / trade name" required className="sm:col-span-2" value={f.name} onChange={set('name')} placeholder={isCust ? 'ABC Auto Components Pvt Ltd' : 'Precision Tools Trading Co.'} data-testid={`${t}-name`} />
          <Select label="Segment" value={f.segment} onChange={set('segment')} data-testid={`${t}-segment`}>
            <option value="">Select</option>{(isCust ? CUSTOMER_SEGMENTS : VENDOR_SEGMENTS).map((s) => <option key={s}>{s}</option>)}
          </Select>
        </div>
      </Section>

      <Section title="GST & Tax">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select label="Registration type" value={f.gst_type} onChange={set('gst_type')} data-testid={`${t}-gst-type`}>{GST_TYPES.map((g) => <option key={g}>{g}</option>)}</Select>
          <Input label="GSTIN" value={f.gstin} onChange={onGstin} maxLength={15} placeholder="27AAAAA0000A1Z5" hint="PAN & state auto-fill from GSTIN" data-testid={`${t}-gstin`} />
          <Input label="PAN" value={f.pan} onChange={(e) => setV('pan', e.target.value.toUpperCase())} maxLength={10} placeholder="AAAAA0000A" data-testid={`${t}-pan`} />
          {!isCust && <Input label="MSME / Udyam no." value={f.msme_no} onChange={set('msme_no')} placeholder="UDYAM-MH-00-0000000" data-testid={`${t}-msme`} />}
        </div>
      </Section>

      <Section title="Address">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input label="Address line 1" className="sm:col-span-2" value={f.address1} onChange={set('address1')} placeholder="Plot / Building / Street" data-testid={`${t}-address1`} />
          <Input label="Address line 2" className="sm:col-span-2" value={f.address2} onChange={set('address2')} placeholder="Area / Industrial estate" data-testid={`${t}-address2`} />
          <Input label="City" value={f.city} onChange={set('city')} placeholder="Pune" data-testid={`${t}-city`} />
          <Picker label="State" value={f.state} onChange={(v) => setF((s) => ({ ...s, state: v, state_code: stateCode(v) }))} options={STATE_OPTS} placeholder="Select state" testid={`${t}-state`} />
          <Input label="PIN code" value={f.pincode} onChange={set('pincode')} maxLength={6} placeholder="411001" data-testid={`${t}-pincode`} />
          <Input label="Country" value={f.country} onChange={set('country')} data-testid={`${t}-country`} />
        </div>
        <label className="inline-flex items-center gap-2 mt-3 text-sm font-semibold cursor-pointer" data-testid={`${t}-ship-same`}>
          <input type="checkbox" checked={f.ship_same} onChange={(e) => setV('ship_same', e.target.checked)} className="h-4 w-4 accent-[#2452c9]" /> {isCust ? 'Shipping' : 'Pickup / dispatch'} address same as above
        </label>
        {!f.ship_same && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3 p-4 rounded-xl" style={{ background: '#f5f7fb' }}>
            <Input label="Ship address line 1" className="sm:col-span-2" value={f.ship_address1} onChange={set('ship_address1')} data-testid={`${t}-ship-address1`} />
            <Input label="Ship address line 2" className="sm:col-span-2" value={f.ship_address2} onChange={set('ship_address2')} data-testid={`${t}-ship-address2`} />
            <Input label="City" value={f.ship_city} onChange={set('ship_city')} data-testid={`${t}-ship-city`} />
            <Picker label="State" value={f.ship_state} onChange={(v) => setV('ship_state', v)} options={STATE_OPTS} placeholder="Select state" testid={`${t}-ship-state`} />
            <Input label="PIN code" value={f.ship_pincode} onChange={set('ship_pincode')} maxLength={6} data-testid={`${t}-ship-pincode`} />
          </div>
        )}
      </Section>

      <Section title="Contact Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input label="Contact person" value={f.contact} onChange={set('contact')} placeholder="Ravi Singh" data-testid={`${t}-contact`} />
          <Input label="Designation" value={f.designation} onChange={set('designation')} placeholder="Purchase Manager" data-testid={`${t}-designation`} />
          <Input label="Phone" value={f.phone} onChange={set('phone')} placeholder="+91 98765 43210" data-testid={`${t}-phone`} />
          <Input label="Alternate phone" value={f.alt_phone} onChange={set('alt_phone')} data-testid={`${t}-alt-phone`} />
          <Input label="WhatsApp" value={f.whatsapp} onChange={set('whatsapp')} placeholder="For dispatch intimation" data-testid={`${t}-whatsapp`} />
          <Input label="Email" type="email" value={f.email} onChange={set('email')} placeholder="accounts@company.com" data-testid={`${t}-email`} />
          <Input label="Website" value={f.website} onChange={set('website')} placeholder="www.company.com" className="sm:col-span-2" data-testid={`${t}-website`} />
        </div>
      </Section>

      <Section title="Credit & Payment Terms">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input label="Credit limit (₹)" type="number" min={0} value={f.credit_limit} onChange={set('credit_limit')} placeholder="0 = no limit" data-testid={`${t}-credit-limit`} />
          <Input label="Credit days" type="number" min={0} value={f.credit_days} onChange={set('credit_days')} data-testid={`${t}-credit-days`} />
          <Select label="Payment terms" value={f.payment_terms} onChange={set('payment_terms')} data-testid={`${t}-payment-terms`}>{PAYMENT_TERMS.map((p) => <option key={p}>{p}</option>)}</Select>
        </div>
      </Section>

      {isCust ? (
        <Section title="Estimated Business Potential" hint="Annual potential (₹) per category — optional, drives sales planning">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {POTENTIAL_CATEGORIES.map((c) => (
              <Input key={c} label={c} type="number" min={0} value={f.potential[c]} onChange={(e) => setF((s) => ({ ...s, potential: { ...s.potential, [c]: e.target.value } }))} placeholder="₹ / year" data-testid={`${t}-potential-${c.replace(/[^a-z]/gi, '').toLowerCase()}`} />
            ))}
          </div>
          <p className="text-sm mt-2 font-semibold" data-testid={`${t}-potential-total`}>Total potential: <span style={{ color: 'var(--erp-navy)' }}>{fmtInr(potTotal)} / year</span></p>
        </Section>
      ) : (
        <Section title="Bank Details" hint="Used for vendor payments">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input label="Bank name" value={f.bank_name} onChange={set('bank_name')} data-testid={`${t}-bank-name`} />
            <Input label="Account number" value={f.bank_account} onChange={set('bank_account')} data-testid={`${t}-bank-account`} />
            <Input label="IFSC" value={f.bank_ifsc} onChange={(e) => setV('bank_ifsc', e.target.value.toUpperCase())} maxLength={11} data-testid={`${t}-bank-ifsc`} />
          </div>
        </Section>
      )}

      <Section title="Notes">
        <Textarea value={f.notes} onChange={set('notes')} placeholder="Internal remarks" data-testid={`${t}-notes`} />
      </Section>
    </DocForm>
  );
};
